import { confirm } from "@inquirer/prompts";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { agentExists, deleteAgent } from "../../lib/foundry-agents.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class CoderTeardownCommand extends M8tCommand {
  static paths = [["coder", "teardown"]];
  static usage = Command.Usage({
    description: "Delete a deployed hosted coder (removes its container + identity + role assignment).",
    details:
      "Idempotent: tearing down a missing coder reports 'already gone'. Only the named agent is touched. Discovery self-heals on the next worker list.",
    examples: [
      ["Tear down a coder", "$0 coder teardown my-coder"],
      ["Skip the confirm (scripts)", "$0 coder teardown my-coder --yes"],
    ],
  });

  name = Option.String();
  yes = Option.Boolean("--yes", false);
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: this.endpoint,
    });

    const exists = await agentExists({ credential, endpoint: project.endpoint, name: this.name });
    if (!exists) {
      if (mode === "json") {
        this.context.stdout.write(
          renderJson({ name: this.name, deleted: false, reason: "not_found", message: "already gone" }) + "\n",
        );
        return 0;
      }
      this.context.stdout.write(`${colors.hint("·")} coder ${colors.field(this.name)} not found (already gone).\n`);
      return 0;
    }

    if (!this.yes) {
      if (!interactive) {
        throw new LocalCliError({
          code: "USAGE",
          message: "teardown requires --yes when running non-interactively.",
          hint: "Re-run with --yes: m8t coder teardown <name> --yes",
        });
      }
      const ok = await confirm({
        message: `Delete hosted coder '${this.name}'? This removes its container and identity (and any active sessions).`,
        default: false,
      });
      if (!ok) {
        this.context.stdout.write("cancelled — nothing removed.\n");
        return 2;
      }
    }

    await deleteAgent({ credential, endpoint: project.endpoint, name: this.name });

    if (mode === "json") {
      this.context.stdout.write(renderJson({ name: this.name, deleted: true }) + "\n");
      return 0;
    }
    this.context.stdout.write(
      `${colors.success("✓")} removed hosted coder ${colors.field(this.name)} (container + identity + role assignment cascaded).\n`,
    );
    this.context.stderr.write(
      `  ${colors.hint("note:")} this removes only the agent. For a full cascade (bindings, a2a, brain), use 'm8t agent remove ${this.name}'.\n`,
    );
    return 0;
  }
}
